/**
 * Ações do card de negociação no painel de contato: Kanban, duplicar, excluir.
 * Reutiliza duplicateLeadAsCopy e deleteLead do CRM (mesma base do Kanban).
 */
import { useCallback } from 'react';
import { Lead, FunnelStage } from '../../../types';
import { useAppContext } from '../../../contexts/AppContext';
import { useCrmData } from '../../../contexts/CrmProviders';
import { duplicateLeadAsCopy } from '../../../features/kanban/utils/leadFormHelpers';
import { supabase } from '../../../lib/supabase';

interface UseDealCardActionsParams {
    lead: Lead;
    funnelStages: FunnelStage[];
}

export function useDealCardActions({ lead, funnelStages }: UseDealCardActionsParams) {
    const { setCurrentPage } = useAppContext();
    const {
        contacts,
        saveLead,
        deleteLead,
        refreshLeads,
        selectedFunnelId,
        setSelectedFunnelId,
    } = useCrmData();

    const handleToKanban = useCallback(async () => {
        localStorage.setItem('KANBAN_OPEN_LEAD_ID', lead.id);

        let funnelForRefresh: string | null | undefined = selectedFunnelId ?? undefined;
        const stageInPanelFunnel =
            !!lead.stage && funnelStages.some(s => s.id === lead.stage);

        if (!stageInPanelFunnel && lead.stage) {
            const { data: stageRow } = await supabase
                .from('funnel_stages')
                .select('funnel_id')
                .eq('id', lead.stage)
                .maybeSingle();
            if (stageRow?.funnel_id) {
                funnelForRefresh = stageRow.funnel_id;
                setSelectedFunnelId(stageRow.funnel_id);
            }
        }

        await refreshLeads(
            funnelForRefresh !== undefined ? funnelForRefresh : selectedFunnelId ?? null
        );
        setCurrentPage('kanban');
    }, [
        lead.id,
        lead.stage,
        funnelStages,
        selectedFunnelId,
        refreshLeads,
        setCurrentPage,
        setSelectedFunnelId
    ]);

    const handleDuplicate = useCallback(async () => {
        await duplicateLeadAsCopy(
            lead,
            contacts,
            funnelStages,
            selectedFunnelId,
            saveLead
        );
    }, [lead, contacts, funnelStages, selectedFunnelId, saveLead]);

    const handleDeleteConfirmed = useCallback(async () => {
        await deleteLead(lead.id);
        await refreshLeads(selectedFunnelId ?? undefined);
    }, [deleteLead, lead.id, refreshLeads, selectedFunnelId]);

    return {
        handleToKanban,
        handleDuplicate,
        handleDeleteConfirmed
    };
}
